/**
 * Collection Modal Manager
 */

export class CollectionModal {
    constructor(options = {}) {
        this.options = {
            modalId: 'collection-modal',
            breakpoints: {
                mobile: 0,
                tablet: 768,
                desktop: 1024,
            },
            ...options,
        };

        this.modal = null;
        this.overlay = null;
        this.container = null;
        this.closeBtn = null;
        this.isOpen = false;
        this.currentFdiId = null;
        this.currentFdiData = null;
        this.touchStartY = 0;
        this.touchEndY = 0;
        this.emitter = new EventTarget();

        this.init();
    }

    on(event, callback) {
        const handler = (evt) => callback(evt.detail);
        this.emitter.addEventListener(event, handler);
        return () => this.emitter.removeEventListener(event, handler);
    }

    emit(event, detail = {}) {
        this.emitter.dispatchEvent(new CustomEvent(event, { detail }));
    }

    init() {
        this.modal = document.getElementById(this.options.modalId);

        if (!this.modal) {
            console.error('Collection modal element not found');
            return;
        }

        this.overlay = this.modal.querySelector('[data-collection-overlay]');
        this.container = this.modal.querySelector('[data-collection-container]');
        this.closeBtn = this.modal.querySelector('[data-collection-close]');

        this.attachEventListeners();
        this.setupTouchGestures();
    }

    attachEventListeners() {
        if (this.closeBtn) {
            this.closeBtn.addEventListener('click', () => this.close());
        }

        if (this.overlay) {
            this.overlay.addEventListener('click', () => this.close());
        }

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.isOpen) {
                this.close();
            }
        });

        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                if (this.isOpen) {
                    this.handleResize();
                }
            }, 150);
        });
    }

    setupTouchGestures() {
        if (!this.container) return;

        this.container.addEventListener('touchstart', (event) => {
            this.touchStartY = event.touches[0].clientY;
        }, { passive: true });

        this.container.addEventListener('touchmove', (event) => {
            if (!this.isMobile()) return;

            const touchY = event.touches[0].clientY;
            const diff = touchY - this.touchStartY;

            if (diff > 0) {
                this.container.style.transform = `translateY(${diff}px)`;
            }
        }, { passive: true });

        this.container.addEventListener('touchend', (event) => {
            if (!this.isMobile()) return;

            this.touchEndY = event.changedTouches[0].clientY;
            const diff = this.touchEndY - this.touchStartY;

            if (diff > 100) {
                this.close();
            } else {
                this.container.style.transform = '';
            }
        }, { passive: true });
    }

    async open(fdiId) {
        if (!this.modal || !fdiId) return;

        this.currentFdiId = Number(fdiId);
        this.currentFdiData = null;
        
        if (this.container) {
            this.container.setAttribute('data-fdi-id', String(this.currentFdiId));
        }

        // Update modal header with loading state
        this.updateModalHeader(null, true);

        this.modal.classList.remove('hidden');
        this.modal.setAttribute('aria-hidden', 'false');

        requestAnimationFrame(() => {
            if (this.overlay) {
                this.overlay.classList.remove('opacity-0');
                this.overlay.classList.add('opacity-100');
            }

            if (!this.container) return;

            if (this.isMobile()) {
                this.container.classList.remove('translate-y-full');
                this.container.classList.add('translate-y-0');
            } else if (this.isDesktop()) {
                this.container.classList.remove('translate-x-full');
                this.container.classList.add('translate-x-0');
            } else {
                this.container.classList.remove('scale-95', 'translate-y-full');
                this.container.classList.add('scale-100', 'translate-y-0');
            }
        });

        document.body.style.overflow = 'hidden';
        this.isOpen = true;
        
        // Fetch FDI item data
        try {
            const response = await fetch(`${window.iphubCollections?.restUrl || '/wp-json/iphub/v1'}/fdi/${this.currentFdiId}`, {
                method: 'GET',
                headers: {
                    'X-WP-Nonce': window.iphubCollections?.nonce || '',
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                this.currentFdiData = data;
                this.updateModalHeader(data, false);
            } else {
                this.updateModalHeader(null, false);
            }
        } catch (error) {
            console.error('Failed to fetch FDI item data:', error);
            this.updateModalHeader(null, false);
        }
        
        this.emit('open', { fdiId: this.currentFdiId, fdiData: this.currentFdiData });
    }

    updateModalHeader(fdiData, isLoading = false) {
        const titleElement = this.modal.querySelector('[data-modal-title]');
        const subtitleElement = this.modal.querySelector('[data-modal-subtitle]');
        
        if (!titleElement) return;
        
        if (isLoading) {
            titleElement.textContent = 'Đang tải...';
            if (subtitleElement) {
                subtitleElement.innerHTML = '<div class="animate-pulse h-4 bg-gray-200 rounded w-3/4"></div>';
            }
            return;
        }
        
        titleElement.textContent = 'Lưu vào danh sách yêu thích';
        
        if (subtitleElement && fdiData) {
            const countryFlag = fdiData.country ?
                `<img src="${fdiData.country.flag_url || ''}" alt="${fdiData.country.name}" class="w-5 h-4 object-cover rounded border border-gray-200" />` : '';
            
            subtitleElement.innerHTML = `
                <div class="flex items-center gap-2 text-sm text-gray-600">
                    ${countryFlag}
                    <span class="font-medium line-clamp-1">${this.escapeHtml(fdiData.title)}</span>
                </div>
            `;
        } else if (subtitleElement) {
            subtitleElement.innerHTML = '';
        }
    }

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    close() {
        if (!this.modal || !this.isOpen) return;

        if (this.overlay) {
            this.overlay.classList.remove('opacity-100');
            this.overlay.classList.add('opacity-0');
        }

        if (this.container) {
            if (this.isMobile()) {
                this.container.classList.remove('translate-y-0');
                this.container.classList.add('translate-y-full');
            } else if (this.isDesktop()) {
                this.container.classList.remove('translate-x-0');
                this.container.classList.add('translate-x-full');
            } else {
                this.container.classList.remove('scale-100', 'translate-y-0');
                this.container.classList.add('scale-95', 'translate-y-full');
            }
        }

        setTimeout(() => {
            this.modal.classList.add('hidden');
            this.modal.setAttribute('aria-hidden', 'true');
            if (this.container) {
                this.container.style.transform = '';
            }
            document.body.style.overflow = '';
        }, 200);

        this.isOpen = false;
        this.emit('close', { fdiId: this.currentFdiId });
        this.currentFdiId = null;
        this.currentFdiData = null;
    }

    handleResize() {
        if (!this.container) return;

        this.container.classList.remove('translate-y-full', 'translate-x-full', 'scale-95', 'scale-100', 'translate-y-0', 'translate-x-0');

        if (this.isMobile()) {
            this.container.classList.add('translate-y-0');
        } else if (this.isDesktop()) {
            this.container.classList.add('translate-x-0');
        } else {
            this.container.classList.add('scale-100');
        }
    }

    isMobile() {
        const width = window.innerWidth;
        return width >= this.options.breakpoints.mobile && width < this.options.breakpoints.tablet;
    }

    isDesktop() {
        const width = window.innerWidth;
        return width >= this.options.breakpoints.desktop;
    }
}

export default CollectionModal;
